// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// =====================================================================================
// D-UI-2 — the automatic layer: a funnel on every column of every table that has none.
//
// WHY. "Every table, no exceptions" cannot be a list of pages somebody remembers to update. The
// next table a developer writes would ship without funnels and nobody would notice until a user
// did. So the default is inverted: a table gets funnels because it is a table. Mounted once at
// the root, this finds every <table> with a header row and gives each labelled column a funnel
// that filters the rows the table is showing. A table written tomorrow has them with no code.
//
// WHAT IT IS NOT. It reads rendered cells, so it can only ever filter rows that are IN the page.
// A table that holds more rows than it renders (client paging, a server page) must filter before
// it pages, which only its own code can do:
//   - rows held in the browser   -> useTableFunnels (TableFunnels.tsx)
//   - rows paged by the server   -> ListTable / funnelToServerOps, against a server allow-list
// Those tables are recognised (they already carry a funnel, or say data-funnels="own") and are
// left alone. If a paged table reaches this layer anyway, the chip row SAYS that only the rows
// on this page are filtered rather than presenting a page as the set.
//
// HOW IT STAYS OUT OF REACT'S WAY. It never edits a node React owns. It appends one host <span>
// to a header cell and renders the funnel into it through a portal; rows are hidden with one
// attribute React does not manage. If React rewrites a header's text and drops the host, the
// observer puts the same host back.
// =====================================================================================

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ColumnFilterButton, ColumnFilterChips, applyColumnFilters, type ColumnFilters } from './ColumnFilter'
import { comparable, distinctTokens, inferKind, type FunnelKind } from './TableFunnels'

const HIDDEN_ATTR = 'data-funnel-hidden'
const HOST_ATTR = 'data-funnel-host'
const CHIPS_ATTR = 'data-funnel-chips-host'
const STYLE_ID = 'owl-funnel-style'
/** Rows read to decide a column's control. Enough to tell a date from a name, cheap on a big table. */
const KIND_SAMPLE = 200

export interface AutoColumn { key: string; label: string; index: number; kind: FunnelKind }

// ---- reading a table (pure: takes elements, touches nothing) --------------------------------

/** Header text as the user reads it: no sort arrows, no control labels, none of our own host. */
export function headerLabel(th: Element): string {
  const clone = th.cloneNode(true) as Element
  clone.querySelectorAll(`[${HOST_ATTR}], button, input, select, textarea, svg, [role="separator"], [aria-hidden="true"]`)
    .forEach(e => e.remove())
  return (clone.textContent ?? '').replace(/[↑↓▲▼↕⇅]/g, '').replace(/\s+/g, ' ').trim()
}

/** What a body cell shows. A cell that is only a control reports the control's value. */
export function cellText(td: Element | undefined): string {
  if (!td) return ''
  const text = (td.textContent ?? '').replace(/\s+/g, ' ').trim()
  if (text && text !== '—' && text !== '-') return text
  const ctl = td.querySelector('input:not([type="hidden"]), select, textarea') as
    (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)
  if (!ctl) return ''
  if (ctl instanceof HTMLInputElement && (ctl.type === 'checkbox' || ctl.type === 'radio'))
    return ctl.checked ? 'Yes' : 'No'
  return String(ctl.value ?? '').trim()
}

/** Column index -> cell, honouring colSpan. */
function cellsByColumn(row: HTMLTableRowElement): (HTMLTableCellElement | undefined)[] {
  const out: (HTMLTableCellElement | undefined)[] = []
  for (const cell of Array.from(row.cells)) {
    const span = Math.max(1, cell.colSpan || 1)
    for (let i = 0; i < span; i++) out.push(i === 0 ? cell : undefined)
  }
  return out
}

/**
 * The header cells that each label exactly ONE column, with that column's index.
 * Taken from the last header row, plus any cell of an earlier row that spans down to it.
 * A group header (colSpan > 1) labels several columns and gets no funnel of its own.
 */
export function headerCells(table: HTMLTableElement): { th: HTMLTableCellElement; index: number }[] {
  const head = table.tHead
  if (!head || head.rows.length === 0) return []
  const rows = Array.from(head.rows)
  const last = rows.length - 1
  const taken: boolean[][] = rows.map(() => [])
  const out: { th: HTMLTableCellElement; index: number }[] = []
  rows.forEach((row, r) => {
    let c = 0
    for (const cell of Array.from(row.cells)) {
      while (taken[r][c]) c++
      const cs = Math.max(1, cell.colSpan || 1), rs = Math.max(1, cell.rowSpan || 1)
      for (let i = r; i < Math.min(rows.length, r + rs); i++)
        for (let j = c; j < c + cs; j++) taken[i][j] = true
      if (cs === 1 && r + rs - 1 >= last) out.push({ th: cell, index: c })
      c += cs
    }
  })
  return out.sort((a, b) => a.index - b.index)
}

/** True when the table brings its own funnels, so this layer must not add a second set. */
export function hasOwnFunnels(table: HTMLTableElement): boolean {
  const mode = table.getAttribute('data-funnels')
  if (mode === 'own' || mode === 'off') return true
  if (table.getAttribute('role') === 'presentation') return true
  const head = table.tHead
  if (!head) return true
  for (const el of Array.from(head.querySelectorAll('button[aria-label^="Filter "], [aria-label$="filter unavailable"]')))
    if (!el.closest(`[${HOST_ATTR}]`)) return true
  return false
}

/**
 * Body rows, each data row with the rows that belong to it.
 * A row that is ONE cell across the table is not a record: it is a detail panel (it follows its
 * record) or an empty/loading state (it stands alone and is never hidden).
 */
export function dataRows(table: HTMLTableElement, columnCount: number): { row: HTMLTableRowElement; tail: HTMLTableRowElement[] }[] {
  const out: { row: HTMLTableRowElement; tail: HTMLTableRowElement[] }[] = []
  for (const body of Array.from(table.tBodies)) {
    for (const row of Array.from(body.rows)) {
      const full = row.cells.length === 1 && (row.cells[0].colSpan || 1) >= Math.max(2, columnCount - 1)
      if (full) { if (out.length) out[out.length - 1].tail.push(row); continue }
      if (row.cells.length === 0) continue
      out.push({ row, tail: [] })
    }
  }
  return out
}

/** The labelled columns of a table, keyed by label (stable across re-renders and reordering). */
export function readColumns(table: HTMLTableElement): (AutoColumn & { th: HTMLTableCellElement })[] {
  const heads = headerCells(table)
  if (heads.length === 0) return []
  const width = Math.max(...heads.map(h => h.index)) + 1
  const sample = dataRows(table, width).slice(0, KIND_SAMPLE).map(r => cellsByColumn(r.row))
  const used = new Map<string, number>()
  const out: (AutoColumn & { th: HTMLTableCellElement })[] = []
  for (const h of heads) {
    // A header that holds no data (row actions, a per-row input) says so; it gets no funnel.
    if (h.th.hasAttribute('data-no-funnel')) continue
    const label = headerLabel(h.th)
    if (!label) continue
    const n = (used.get(label) ?? 0) + 1
    used.set(label, n)
    out.push({
      key: n === 1 ? label : `${label} (${n})`, label, index: h.index, th: h.th,
      kind: inferKind(sample.map(cells => cellText(cells[h.index]))),
    })
  }
  return out
}

/** True when a pager sits next to the table: the table is showing one page of something larger. */
function looksPaged(table: HTMLTableElement): boolean {
  let el: HTMLElement | null = table.parentElement
  for (let i = 0; i < 3 && el; i++, el = el.parentElement)
    if (el.querySelector('[aria-label="Pagination"]')) return true
  return false
}

// ---- the mounted layer ----------------------------------------------------------------------

interface Entry {
  id: string
  table: HTMLTableElement
  columns: (AutoColumn & { th: HTMLTableCellElement })[]
  hosts: HTMLElement[]
  chipsHost: HTMLElement | null
  paged: boolean
  /** Changes when the set of columns or their controls change; drives re-render. */
  sig: string
  shown: number
  total: number
}

let seq = 0
const ids = new WeakMap<HTMLTableElement, string>()
const hostOf = new WeakMap<HTMLTableCellElement, HTMLElement>()
const chipsOf = new WeakMap<HTMLTableElement, HTMLElement>()

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return
  const s = document.createElement('style')
  s.id = STYLE_ID
  s.textContent = `tr[${HIDDEN_ATTR}]{display:none !important}[${HOST_ATTR}]{display:inline-flex;vertical-align:middle;margin-left:2px}`
  document.head.appendChild(s)
}

export default function AutoTableFunnels() {
  const [entries, setEntries] = useState<Entry[]>([])
  const [filters, setFilters] = useState<Record<string, ColumnFilters>>({})
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const entriesRef = useRef<Entry[]>([])

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return
    ensureStyle()
    let frame = 0

    const apply = (e: Entry) => {
      const f = filtersRef.current[e.id] ?? {}
      const active = Object.fromEntries(Object.entries(f).filter(([k, s]) => s.size > 0 && e.columns.some(c => c.key === k)))
      const width = e.columns.length ? Math.max(...e.columns.map(c => c.index)) + 1 : 0
      const rows = dataRows(e.table, width)
      const none = Object.keys(active).length === 0
      const kindOf = new Map(e.columns.map(c => [c.key, c]))
      let shown = 0
      for (const r of rows) {
        let keep = true
        if (!none) {
          const cells = cellsByColumn(r.row)
          keep = applyColumnFilters([r], active, (_row, key) => {
            const c = kindOf.get(key)
            return c ? comparable(c.kind, cellText(cells[c.index])) : null
          }).length === 1
        }
        if (keep) shown++
        for (const tr of [r.row, ...r.tail]) {
          if (keep) { if (tr.hasAttribute(HIDDEN_ATTR)) tr.removeAttribute(HIDDEN_ATTR) }
          else if (!tr.hasAttribute(HIDDEN_ATTR)) tr.setAttribute(HIDDEN_ATTR, '')
        }
      }
      e.shown = shown; e.total = rows.length
      // The chip row lives just above the table, and only while something is filtered.
      if (none) { e.chipsHost?.remove(); e.chipsHost = null; return }
      let host = chipsOf.get(e.table)
      if (!host) { host = document.createElement('div'); host.setAttribute(CHIPS_ATTR, ''); chipsOf.set(e.table, host) }
      if (host.nextSibling !== e.table || host.parentNode !== e.table.parentNode)
        e.table.parentNode?.insertBefore(host, e.table)
      e.chipsHost = host
    }

    const scan = () => {
      frame = 0
      const next: Entry[] = []
      for (const table of Array.from(document.querySelectorAll('table'))) {
        if (!(table instanceof HTMLTableElement) || hasOwnFunnels(table)) continue
        const columns = readColumns(table)
        if (columns.length === 0) continue
        let id = ids.get(table)
        if (!id) { id = `t${++seq}`; ids.set(table, id) }
        const hosts = columns.map(c => {
          let h = hostOf.get(c.th)
          if (!h) {
            h = document.createElement('span')
            h.setAttribute(HOST_ATTR, '')
            hostOf.set(c.th, h)
          }
          if (h.parentNode !== c.th) c.th.appendChild(h)
          return h
        })
        const prev = entriesRef.current.find(e => e.id === id)
        const entry: Entry = {
          id, table, columns, hosts, chipsHost: prev?.chipsHost ?? null, paged: looksPaged(table),
          sig: columns.map(c => `${c.key}:${c.index}:${c.kind}`).join('|'), shown: 0, total: 0,
        }
        apply(entry)
        next.push(entry)
      }
      // A table that left the page takes its chip row with it.
      for (const old of entriesRef.current)
        if (!next.some(e => e.id === old.id)) old.chipsHost?.remove()
      const before = entriesRef.current
      const changed = before.length !== next.length || next.some((e, i) =>
        before[i]?.id !== e.id || before[i]?.sig !== e.sig || before[i]?.shown !== e.shown
        || before[i]?.total !== e.total || before[i]?.chipsHost !== e.chipsHost
        || before[i]?.hosts.some((h, j) => h !== e.hosts[j]))
      entriesRef.current = next
      if (changed) setEntries(next)
    }

    const schedule = () => { if (!frame) frame = requestAnimationFrame(scan) }
    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true, characterData: true })
    // A control inside a cell changes what the cell "shows" without any DOM mutation.
    document.addEventListener('change', schedule, true)
    window.addEventListener('owl:funnels-rescan', schedule)
    schedule()
    return () => {
      observer.disconnect()
      document.removeEventListener('change', schedule, true)
      window.removeEventListener('owl:funnels-rescan', schedule)
      if (frame) cancelAnimationFrame(frame)
      for (const e of entriesRef.current) {
        e.chipsHost?.remove()
        e.hosts.forEach(h => h.remove())
        for (const r of Array.from(e.table.querySelectorAll(`tr[${HIDDEN_ATTR}]`))) r.removeAttribute(HIDDEN_ATTR)
      }
      entriesRef.current = []
    }
  }, [])

  // A selection changed: re-apply on the next frame through the same path a DOM change takes.
  useEffect(() => { window.dispatchEvent(new Event('owl:funnels-rescan')) }, [filters])

  const setFor = (id: string, next: ColumnFilters) => setFilters(all => ({ ...all, [id]: next }))

  return (
    <>
      {entries.map(e => {
        const mine = filters[e.id] ?? {}
        const live: ColumnFilters = Object.fromEntries(
          Object.entries(mine).filter(([k, s]) => s.size > 0 && e.columns.some(c => c.key === k)))
        const width = Math.max(...e.columns.map(c => c.index)) + 1
        return (
          <span key={e.id} style={{ display: 'none' }}>
            {e.columns.map((c, i) => createPortal(
              <ColumnFilterButton
                label={c.label}
                isDate={c.kind === 'date'}
                isNumeric={c.kind === 'number'}
                isText={c.kind === 'text'}
                getValues={() => {
                  // Spreadsheet semantics: this column keeps its full list; the others narrow it.
                  const others: ColumnFilters = { ...live }
                  delete others[c.key]
                  const byKey = new Map(e.columns.map(x => [x.key, x]))
                  const rows = dataRows(e.table, width).map(r => cellsByColumn(r.row))
                  const kept = applyColumnFilters(rows, others, (cells, key) => {
                    const x = byKey.get(key)
                    return x ? comparable(x.kind, cellText(cells[x.index])) : null
                  })
                  return distinctTokens(kept.map(cells => cellText(cells[c.index])))
                }}
                selected={live[c.key] ?? new Set()}
                onChange={next => setFor(e.id, { ...live, [c.key]: next })} />,
              e.hosts[i], `${e.id}:${c.key}`))}
            {e.chipsHost && createPortal(
              <div>
                <ColumnFilterChips
                  filters={live}
                  labels={Object.fromEntries(e.columns.map(c => [c.key, c.label]))}
                  onClear={k => setFor(e.id, { ...live, [k]: new Set() })}
                  onClearAll={() => setFor(e.id, {})} />
                <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '0 0 6px' }}>
                  {e.shown.toLocaleString()} of {e.total.toLocaleString()} rows match
                  {e.paged ? '. These filters cover the rows on this page only.' : ''}
                </div>
              </div>,
              e.chipsHost, `${e.id}:chips`)}
          </span>
        )
      })}
    </>
  )
}
